import { useCallback, useMemo } from "react";
import { LliLinkButton, Particulars as ParticularsGrid } from "@idwal/idwal-react-components";
import { formatNumber } from "../../utils/numberHelpers";
import { ScreeningItem } from "../../types/Screening";
import { formatDecimalAge } from "../../utils/dates";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { sendGAEvent } from "../../services/AnalyticsService";
import { GA_BUTTON_EVENT, GA_CLICK_LLI_LINK } from "../../constants/GaEvents";
import "./ScreeningTabs.css";

type Props = {
    data: ScreeningItem;
};

export const ScreeningParticulars = (props: Props) => {
    const { t } = useTranslation("locale");
    const { data } = props;

    const formatDate = useCallback(
        (date: string) => date ? dayjs(date).format("DD/MM/YYYY") : "",
        [data]
    );

    const formatYesNo = useCallback(
        (yesNo: string) => {
            if (yesNo === "Y") {
                return "Yes";
            }

            if (yesNo === "N") {
                return "No";
            }

            return yesNo;
        },
        [data]
    );

    const formatUnitNumber = useCallback(
        (value: number | null | undefined, unit: string) => {
            if (value) {
                return `${formatNumber(`${value}`)} ${unit}`;
            }

            return "";
        },
        [data]
    );
    const particulars = useMemo(() => {
        const particularsData: Record<string, string> = {
            shipName: data.vessel_name,
            iMONumber: `${data.vessel_imo}`,
            exName: data.ex_name,
            portOfRegistry: data.port_of_registry,
            shipType: data.vessel_type,
            flag: `${data.flag_state}${
                data.flag_code ? ` (${data.flag_code})` : ""
            }`,
            classificationSociety: `${data.class_society}${
                data.class_code ? ` (${data.class_code})` : ""
            }`,
            registeredOwner: data.registered_owner,
            technicalManager: data.technical_manager,
            shipbuilder: data.builder,
            constructionEnd: formatDate(data.construction_end),
            shipAge: formatDecimalAge(data.age_of_vessel),
            countryOfBuild: `${data.build_country}${
                data.build_country_code ? ` (${data.build_country_code})` : ""
            }`,
            operator: data.operator,
            "p&IClub": data.pandi_club,
            gearless: formatYesNo(data.is_gearless),
            deadweight: formatUnitNumber(data.deadweight, "MT"),
            grossTonnage: formatUnitNumber(data.gross_tonnage, "MT"),
            netTonnage: formatUnitNumber(data.net_tonnage, "MT"),
            length: formatUnitNumber(data.length, "m"),
            breadth: formatUnitNumber(data.breadth, "m"),
            depth: formatUnitNumber(data.depth, "m"),
            draught: formatUnitNumber(data.draught, "m"),
            lightweight: formatUnitNumber(data.lightweight, "MT")
        };

        return particularsData;
    }, [data]);

    const gaEvent = useCallback(() => {
        sendGAEvent(GA_BUTTON_EVENT, GA_CLICK_LLI_LINK);
    }, []);

    return (
        <div className="flex flex-column">
            <div className="flex justify-content-between mb-3">
                <p className="psc-header">{t("screeningParticulars.particularsHeader")}</p>
                <LliLinkButton />
            </div>
            <ParticularsGrid particulars={particulars} />
            <div className="flex justify-content-center align-items-center mt-6">
                <span>{t("screeningParticulars.footer")}</span>
            </div>
        </div>
    )
};
